import { EventSeverity, EventStatus, EventType } from "@sitescene/types";

import type { EventsState } from "@/context/events";
import type { EventFilterScope } from "@/features/events/types/event-filter-scope";

import {
	dateValueToCalendarDateString,
	isDateRangeInverted,
	parseCalendarDateRangeBound,
} from "@/utils/date-time";

import type { LocationZoneSensorIdSets } from "./filter-valid-ids";

import { isAllFilterIdsSelected } from "./filter-valid-ids";
import {
	DEFAULT_SEVERITY_FILTER,
	resolveStoredSeverityFilter,
} from "./severity-filter-utils";

/** Filter fields persisted per page (Dashboard vs All Activity). */
export type PersistedEventFilterFields = Pick<
	EventsState,
	| "dateRangeFilter"
	| "eventTypesFilter"
	| "locationsFilter"
	| "searchQueryFilter"
	| "sensorsFilter"
	| "severityFilter"
	| "statusFilter"
	| "zonesFilter"
>;

interface StoredDateTimeRange {
	end: string;
	start: string;
}

interface StoredEventFiltersBlob {
	/** CalendarDateTime strings (`YYYY-MM-DDTHH:mm:ss`). */
	dateTimeRange: null | StoredDateTimeRange;
	eventTypes: EventType[];
	locations: string[];
	searchQuery: string;
	sensors: string[];
	severity: EventSeverity | null;
	/** True when severity was written by the app (distinguishes legacy null from Any Priority). */
	severityExplicit?: boolean;
	status: EventStatus | null;
	zones: string[];
}

const SCOPE_STORAGE_SUFFIX: Record<EventFilterScope, string> = {
	"all-activity": "-all-activity-events-filters",
	dashboard: "-dashboard-events-filters",
};

export const getEventFiltersStorageKey = (
	orgCode: string,
	scope: EventFilterScope,
) => `${orgCode}${SCOPE_STORAGE_SUFFIX[scope]}`;

/** Legacy shared key before per-page scopes existed. */
export const getLegacyEventFiltersStorageKey = (orgCode: string) =>
	`${orgCode}-events-filters`;

const isRecognizedEventFiltersStorageKey = (key: string): boolean =>
	Object.values(SCOPE_STORAGE_SUFFIX).some((suffix) => key.endsWith(suffix));

const isLegacyEventFiltersStorageKey = (key: string): boolean =>
	key.endsWith("-events-filters") &&
	!key.endsWith(SCOPE_STORAGE_SUFFIX.dashboard) &&
	!key.endsWith(SCOPE_STORAGE_SUFFIX["all-activity"]);

export const clearStoredEventFilters = (
	orgCode: string,
	scope: EventFilterScope,
): void => {
	if (!orgCode) return;
	localStorage.removeItem(getEventFiltersStorageKey(orgCode, scope));
};

/** Default dashboard filter fields, with full L/Z/S selection when the org tree is known. */
export const getDefaultStoredEventFilters = (
	idSets: LocationZoneSensorIdSets,
): PersistedEventFilterFields => ({
	dateRangeFilter: null,
	eventTypesFilter: [],
	locationsFilter: idSets.locationIds.length > 0 ? [...idSets.locationIds] : [],
	searchQueryFilter: "",
	sensorsFilter: idSets.sensorIds.length > 0 ? [...idSets.sensorIds] : [],
	severityFilter: DEFAULT_SEVERITY_FILTER,
	statusFilter: null,
	zonesFilter: idSets.zoneIds.length > 0 ? [...idSets.zoneIds] : [],
});

/** Removes every org's persisted event filters from localStorage. */
export const clearAllStoredEventFilters = (): void => {
	const keysToRemove: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (
			key &&
			(isRecognizedEventFiltersStorageKey(key) ||
				isLegacyEventFiltersStorageKey(key))
		) {
			keysToRemove.push(key);
		}
	}
	for (const key of keysToRemove) {
		localStorage.removeItem(key);
	}
};

const isEventSeverity = (value: unknown): value is EventSeverity =>
	typeof value === "string" &&
	Object.values(EventSeverity).includes(value as EventSeverity);

const isEventType = (value: unknown): value is EventType =>
	typeof value === "string" &&
	Object.values(EventType).includes(value as EventType);

const isEventStatus = (value: unknown): value is EventStatus =>
	typeof value === "string" &&
	Object.values(EventStatus).includes(value as EventStatus);

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((item) => typeof item === "string");

const isStoredDateTimeRange = (value: unknown): value is StoredDateTimeRange =>
	typeof value === "object" &&
	value !== null &&
	typeof (value as StoredDateTimeRange).start === "string" &&
	typeof (value as StoredDateTimeRange).end === "string";

const parseStoredStatus = (value: unknown): EventStatus | null => {
	if (value === null) return null;
	return isEventStatus(value) ? value : null;
};

const parseStoredFilters = (raw: unknown): null | StoredEventFiltersBlob => {
	if (!raw || typeof raw !== "object") return null;

	const data = raw as Record<string, unknown>;
	if (!isStringArray(data.locations)) return null;
	if (!isStringArray(data.eventTypes) || !data.eventTypes.every(isEventType)) {
		return null;
	}
	if (!isStringArray(data.sensors) || !isStringArray(data.zones)) return null;
	if (typeof data.searchQuery !== "string") return null;
	if (data.severity !== null && !isEventSeverity(data.severity)) return null;
	// Tolerate legacy blobs without `status`; only reject genuinely invalid values.
	if ("status" in data && data.status !== null && !isEventStatus(data.status)) {
		return null;
	}
	const status = parseStoredStatus(data.status); // undefined → null

	// Only `dateTimeRange` is supported. Older `dateRange`/`timeRange` blobs
	// load with a null datetime filter (other fields still hydrate).
	let dateTimeRange: null | StoredDateTimeRange = null;
	if ("dateTimeRange" in data) {
		if (data.dateTimeRange === null) {
			dateTimeRange = null;
		} else if (isStoredDateTimeRange(data.dateTimeRange)) {
			dateTimeRange = data.dateTimeRange;
		} else {
			return null;
		}
	}

	const blob: StoredEventFiltersBlob = {
		dateTimeRange,
		eventTypes: data.eventTypes as EventType[],
		locations: data.locations as string[],
		searchQuery: data.searchQuery as string,
		sensors: data.sensors as string[],
		severity: data.severity as EventSeverity | null,
		status,
		zones: data.zones as string[],
	};

	if (data.severityExplicit === true) {
		blob.severityExplicit = true;
	}

	return blob;
};

const readBlobFromKey = (key: string): null | StoredEventFiltersBlob => {
	try {
		const stored = localStorage.getItem(key);
		if (!stored) return null;

		return parseStoredFilters(JSON.parse(stored));
	} catch {
		return null;
	}
};

/** Copies a legacy shared blob into any scoped keys that are still empty. */
const migrateLegacyEventFiltersBlob = (orgCode: string): void => {
	const legacyKey = getLegacyEventFiltersStorageKey(orgCode);
	const legacyBlob = readBlobFromKey(legacyKey);
	if (!legacyBlob) {
		if (localStorage.getItem(legacyKey) !== null) {
			localStorage.removeItem(legacyKey);
		}
		return;
	}

	for (const scope of Object.keys(SCOPE_STORAGE_SUFFIX) as EventFilterScope[]) {
		if (!readBlobFromKey(getEventFiltersStorageKey(orgCode, scope))) {
			writeParsedBlob(orgCode, scope, legacyBlob);
		}
	}

	localStorage.removeItem(legacyKey);
};

const readParsedBlob = (
	orgCode: string,
	scope: EventFilterScope,
): null | StoredEventFiltersBlob => {
	if (!orgCode) return null;

	migrateLegacyEventFiltersBlob(orgCode);

	return readBlobFromKey(getEventFiltersStorageKey(orgCode, scope));
};

const writeParsedBlob = (
	orgCode: string,
	scope: EventFilterScope,
	blob: StoredEventFiltersBlob,
): void => {
	if (!orgCode) return;

	try {
		localStorage.setItem(
			getEventFiltersStorageKey(orgCode, scope),
			JSON.stringify(blob),
		);
	} catch {
		// Ignore storage errors (e.g. quota exceeded)
	}
};

const storedToFilterFields = (
	stored: StoredEventFiltersBlob,
): PersistedEventFilterFields => {
	let dateRangeFilter: PersistedEventFilterFields["dateRangeFilter"] = null;
	if (stored.dateTimeRange) {
		const start = parseCalendarDateRangeBound(
			stored.dateTimeRange.start,
			"start",
		);
		const end = parseCalendarDateRangeBound(stored.dateTimeRange.end, "end");
		if (start && end && !isDateRangeInverted({ end, start })) {
			dateRangeFilter = { end, start };
		}
	}

	return {
		dateRangeFilter,
		eventTypesFilter: stored.eventTypes,
		locationsFilter: stored.locations,
		searchQueryFilter: stored.searchQuery,
		sensorsFilter: stored.sensors,
		severityFilter: resolveStoredSeverityFilter(stored),
		statusFilter: stored.status,
		zonesFilter: stored.zones,
	};
};

const filterFieldsToStored = (
	filters: PersistedEventFilterFields,
): StoredEventFiltersBlob => ({
	dateTimeRange: filters.dateRangeFilter
		? {
				end: dateValueToCalendarDateString(filters.dateRangeFilter.end),
				start: dateValueToCalendarDateString(filters.dateRangeFilter.start),
			}
		: null,
	eventTypes: filters.eventTypesFilter,
	locations: filters.locationsFilter,
	searchQuery: filters.searchQueryFilter,
	sensors: filters.sensorsFilter,
	severity: filters.severityFilter,
	severityExplicit: true,
	status: filters.statusFilter,
	zones: filters.zonesFilter,
});

export const readStoredEventFilters = (
	orgCode: string,
	scope: EventFilterScope,
): null | PersistedEventFilterFields => {
	const parsed = readParsedBlob(orgCode, scope);
	return parsed ? storedToFilterFields(parsed) : null;
};

/** `[]` when `ids` selects every ID in the org, otherwise `ids` unchanged. */
const toStoredIds = (ids: string[], allIds: readonly string[]): string[] =>
	allIds.length > 0 && isAllFilterIdsSelected(ids, allIds) ? [] : ids;

/**
 * Persist filters. With `idSets`, a location / zone / sensor filter that
 * selects the whole org is stored as `[]` ("all"), so a reload also covers
 * anything added to the org after it was saved.
 */
export const writeStoredEventFilters = (
	orgCode: string,
	scope: EventFilterScope,
	filters: PersistedEventFilterFields,
	idSets?: LocationZoneSensorIdSets,
): void => {
	const toStore = idSets
		? {
				...filters,
				locationsFilter: toStoredIds(
					filters.locationsFilter,
					idSets.locationIds,
				),
				sensorsFilter: toStoredIds(filters.sensorsFilter, idSets.sensorIds),
				zonesFilter: toStoredIds(filters.zonesFilter, idSets.zoneIds),
			}
		: filters;
	writeParsedBlob(orgCode, scope, filterFieldsToStored(toStore));
};
